import memoize from 'transmute/memoize';
import uniqueIdForObject from 'automation-ui-core/utils/uniqueIdForObject';
import { calculateAdjacencyMap } from 'automation-ui-directed-graph/DirectedNodes';
import { calculateNodeWidthMultipliers } from 'automation-ui-directed-graph/utils/calculateNodeWidthMultipliers';
import { LEFT_CANVAS_PADDING, CARD_WIDTH_PADDING, SINGLE_CONNECTOR_HEIGHT, BRANCH_CONNECTOR_HEIGHT } from 'automation-ui-flow-core/canvas/graphConstants';

/**
 * Optimistic algorithm that uses current observed heights
 * of nodes to calculate the absolute locations of nodes.
 * Node coordinates are the center of the node.
 * This function will always have exact horizontal coords
 * but will only have estimates for the heights until
 * those nodes are rendered to the page. This is because
 * heights are dynamic.
 *
 * The recursion logic leads to a DFS starting at
 * the startingNodeId and ending at each of the
 * leaf nodes.
 * @param {DirectedNodes} directedGraph
 * @param {Map<NodeId, number>} nodeHeights Keyed off by node id, contains
 *                          heights in pixels
 */
const calculateNodePositions = memoize((directedGraph, nodeHeights) => {
  const nodeWrapperWidthMultipliers = calculateNodeWidthMultipliers(directedGraph.getEdges());
  const adjacencyMap = calculateAdjacencyMap(directedGraph.getEdges());
  let nodePositions = new Map();
  const nodesToVisit = [{
    nodeId: directedGraph.getStartingNodeId(),
    nodeWrapperX: LEFT_CANVAS_PADDING,
    nodeWrapperY: 0
  }];
  while (nodesToVisit.length > 0) {
    var _ref, _nodeHeights$get;
    const {
      nodeId,
      nodeWrapperX,
      nodeWrapperY
    } = nodesToVisit.pop();
    const nodeEdges = adjacencyMap.get(nodeId);
    const widthMultiplier = nodeWrapperWidthMultipliers[nodeId] || 1;
    const width = widthMultiplier * (directedGraph.nodeWidth + CARD_WIDTH_PADDING);
    const height = (_ref = (_nodeHeights$get = nodeHeights.get(`node-${nodeId}`)) !== null && _nodeHeights$get !== void 0 ? _nodeHeights$get : directedGraph.getDefaultNodeHeight(nodeId)) !== null && _ref !== void 0 ? _ref : 0;
    const cardXPos = nodeWrapperX + width / 2 - directedGraph.nodeWidth / 2;
    const numOfChildren = nodeEdges ? nodeEdges.length : 0;
    const connectorHeight = numOfChildren > 1 ? BRANCH_CONNECTOR_HEIGHT : SINGLE_CONNECTOR_HEIGHT;
    const nextNodeWrapperY = nodeWrapperY + height + connectorHeight;
    nodePositions = nodePositions.set(nodeId, {
      x: cardXPos - LEFT_CANVAS_PADDING,
      y: nodeWrapperY,
      outerX: nodeWrapperX - LEFT_CANVAS_PADDING,
      outerY: nextNodeWrapperY
    });
    if (!nodeEdges || Object.hasOwnProperty.call(directedGraph.goToPaths, nodeId)) {
      continue;
    }
    let currentNodeWrapperX = nodeWrapperX;
    nodeEdges.forEach(edge => {
      const childNodeId = edge.getToNode();
      const childNodeWrapperWidthMultiplier = nodeWrapperWidthMultipliers[childNodeId] || 1;
      const childWrapperWidth = childNodeWrapperWidthMultiplier * (directedGraph.nodeWidth + CARD_WIDTH_PADDING);
      if (childNodeId) {
        nodesToVisit.push({
          nodeId: childNodeId,
          nodeWrapperX: currentNodeWrapperX,
          nodeWrapperY: nextNodeWrapperY
        });
      }
      currentNodeWrapperX += childWrapperWidth;
    });
  }
  return nodePositions;
}, (directedGraph, nodeHeights) => `${uniqueIdForObject(nodeHeights)}-${uniqueIdForObject(directedGraph)}`);
export default calculateNodePositions;